import Axios from 'axios';
import axiosRetry from 'axios-retry';
import { windowStore } from 'util/windowStore';
import { productTypes, APP_NAME } from 'appConstants';
import { sumoLogger } from 'sumoLogger';
import config from 'config';
import { path, getToken, StatusCode, RETRY_ATTEMPS, RETRY_DELAY_MSEC } from './';
import { getAccountId, clearDefaults, retry } from './axiosConfig';

const isEuHosted = window.location.hostname.startsWith('eu');
const apiBaseUrl = isEuHosted ? config.euApiRootUrl : config.apiRootUrl;

export const formAxios = Axios.create({
  baseURL: `${apiBaseUrl}v1/textrecruit`,
  responseType: 'json',
  headers: {
    Accept: 'application/json',
    'content-type': 'application/x-www-form-urlencoded',
  },
  params: {
    client_ver: config.clientVersion,
    app_name: APP_NAME,
    product: productTypes.TEXT_RECRUIT,
  },
  paramsSerializer: (params) => {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== null && typeof value !== 'undefined') {
        searchParams.append(key, typeof value === 'object' ? JSON.stringify(value) : value);
      }
    });
    return searchParams.toString();
  },
});

axiosRetry(formAxios, {
  retries: RETRY_ATTEMPS,
  retryCondition: (error) => retry(error),
  retryDelay: () => RETRY_DELAY_MSEC,
});

/**
 * ********************************************
 * *********** REQUEST INTERCEPTORS ***********
 * ********************************************
 */
formAxios.interceptors.request.use(
  async (conf) => {
    const onLookup = conf.url.includes(path.LOOKUP);
    const onAuthInfo = conf.url.includes(path.REMOTE_AUTH_INFO);
    const onAuthenticate = conf.url.includes(path.AUTHENTICATE);
    /**
     * No need to retrieve auth token(s) for:
     * - Lookup service: /lookup
     * - SSO: /remote_auth_info
     * - Login: /authenticate
     */
    if (!onLookup && !onAuthInfo && !onAuthenticate) {
      // Set http headers with required token(s)
      await getToken(conf);
    }
    return conf;
  },
  (error) => Promise.reject(error),
);

/**
 * ********************************************
 * ********** RESPONSE INTERCEPTORS ***********
 * ********************************************
 */
formAxios.interceptors.response.use(
  (response) => {
    const { url } = response.config;
    const { token, userName } = response.data || {};
    if (url.includes(path.AUTHENTICATE) && !windowStore.auth0) {
      windowStore.setToken(token);
    }
    if (url.includes(path.TOKEN_INFO) || url.includes(path.AUTHENTICATE)) {
      const accountId = getAccountId(response.data);
      formAxios.defaults.params.account_id = accountId;
      formAxios.defaults.params.customer_id = accountId;
      windowStore.username = userName;
    }
    if (url.includes(path.LOGOUT)) {
      clearDefaults();
    }
    return response.data;
  },
  (error) => {
    const errorResp = error.response;
    if (errorResp && errorResp !== StatusCode.BAD) {
      sumoLogger.log({
        error: errorResp.data,
        userAgent: navigator.userAgent,
        params: error.config.params,
        apiUrl: error.config.url,
      });
    }

    if (error.message === 'Network Error') {
      return Promise.reject(error.toJSON());
    } else if (errorResp) {
      let errorData = {};
      if (typeof errorResp.data === 'string') {
        errorData.message = errorResp.data;
      } else if (errorResp.data === null) {
        errorData.message = 'Error';
      } else if (errorResp.data.apiResult) {
        errorData = { ...errorResp.data.apiResult };
      } else {
        errorData = { ...errorResp.data };
      }
      return Promise.reject(errorData);
    }
    return Promise.reject(error);
  },
);
